import { useState, useEffect, useCallback, useRef } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { RefreshCw, Utensils, Clock, User, Bell } from 'lucide-react';
import api from '@/lib/api';

/**
 * Cashier "Open Checks" — active waiter/table checks for the CURRENT branch.
 * Reuses GET /cashier/orders?status=open. "Open & Pay" loads the SAME pos_order
 * into the cart (no copy); payment goes through the existing SplitPaymentDialog
 * and the canonical finalize (order_id). No second order/payment engine.
 */
function since(ts) {
  if (!ts) return '';
  const mins = Math.max(0, Math.round((Date.now() - new Date(ts).getTime()) / 60000));
  return mins < 60 ? `${mins}m` : `${Math.floor(mins / 60)}h ${mins % 60}m`;
}

export default function OpenChecksDialog({ open, onOpenChange, branchId, onOpenCheck, onVoidCheck, refreshKey }) {
  const [checks, setChecks] = useState([]);
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    if (!branchId) return;
    setLoading(true);
    try {
      const token = localStorage.getItem('cashier_token');
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      const { data } = await api.get('/cashier/orders', {
        params: { branch_id: branchId, status: 'open' }, headers,
      });
      setChecks(Array.isArray(data) ? data : []);
    } catch { setChecks([]); }
    setLoading(false);
  }, [branchId]);

  useEffect(() => { if (open) load(); }, [open, load, refreshKey]);

  // Phase A — realtime. Reuse the live ws:dashboard bus the POS socket already
  // re-broadcasts as a window CustomEvent (no second socket, no polling, no new
  // events). While the dialog is open, a relevant waiter/kitchen/payment/sale
  // event for this branch debounce-reloads the SAME GET /cashier/orders?status=open
  // so the list always mirrors the canonical pos_order. One listener, cleaned up
  // on close/unmount.
  const debounceRef = useRef(null);
  useEffect(() => {
    if (!open) return undefined;
    const RELEVANT = new Set(['check_opened', 'check_items_added', 'check_payment_requested', 'new_sale', 'check_voided', 'check_items_cancelled']);
    const onWs = (e) => {
      const data = e?.detail || {};
      const type = data.type || data.event;
      if (!RELEVANT.has(type)) return;
      if (data.branch_id && branchId && data.branch_id !== branchId) return;
      if (debounceRef.current) clearTimeout(debounceRef.current);
      debounceRef.current = setTimeout(() => { load(); }, 400);
    };
    window.addEventListener('ws:dashboard', onWs);
    return () => {
      window.removeEventListener('ws:dashboard', onWs);
      if (debounceRef.current) { clearTimeout(debounceRef.current); debounceRef.current = null; }
    };
  }, [open, branchId, load]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="open-checks-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center justify-between">
            <span className="flex items-center gap-2"><Utensils className="w-5 h-5 text-orange-600" /> Open Checks</span>
            <Button size="sm" variant="ghost" onClick={load} data-testid="open-checks-refresh">
              <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
            </Button>
          </DialogTitle>
        </DialogHeader>

        {checks.length === 0 ? (
          <div className="py-10 text-center text-sm text-muted-foreground" data-testid="open-checks-empty">
            No open checks for this branch.
          </div>
        ) : (
          <div className="space-y-2">
            {checks.map((c) => {
              const itemCount = (c.items || []).reduce((n, it) => n + (it.quantity || 0), 0);
              return (
                <div key={c.id}
                  className={`rounded-xl border p-3 flex items-center justify-between gap-3 ${c.payment_requested ? 'border-amber-400 bg-amber-50' : 'bg-white'}`}
                  data-testid={`open-check-${c.id}`}>
                  <div className="min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-semibold">{c.is_held ? `Held bill${c.customer_phone ? ' · ' + c.customer_phone : ''}` : `Table ${c.table_number || '—'}`}</span>
                      {c.check_label && <Badge variant="secondary" data-testid={`check-label-${c.id}`}>{c.check_label}</Badge>}
                      {c.payment_requested && (
                        <Badge className="bg-amber-500 text-white" data-testid={`payment-requested-${c.id}`}>
                          <Bell className="w-3 h-3 mr-1" /> Payment Requested
                        </Badge>
                      )}
                    </div>
                    <div className="text-xs text-muted-foreground mt-1 flex items-center gap-3 flex-wrap">
                      <span>{c.order_number ? `#${c.order_number}` : 'Held'}</span>
                      <span className="flex items-center gap-1"><User className="w-3 h-3" />{c.waiter_id ? 'Waiter' : '—'}</span>
                      <span className="flex items-center gap-1"><Clock className="w-3 h-3" />{since(c.created_at)}</span>
                      <span>{itemCount} item{itemCount === 1 ? '' : 's'}</span>
                    </div>
                  </div>
                  <div className="text-right shrink-0">
                    <div className="font-bold tabular-nums">{Number(c.total || 0).toFixed(2)}</div>
                    <Button size="sm" className="mt-1 bg-orange-500 hover:bg-orange-600"
                      onClick={() => { onOpenCheck(c); onOpenChange(false); }}
                      data-testid={`open-check-pay-${c.id}`}>
                      Open & Pay
                    </Button>
                    {onVoidCheck && (
                      <Button size="sm" variant="outline" className="mt-1 ml-2 h-10 text-red-600 border-red-200"
                        onClick={() => onVoidCheck(c)} data-testid={`open-check-void-${c.id}`}>
                        Void
                      </Button>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
